import {
  FileNameWithImage,
  MSPQRadioGroupType,
  MSPQCheckBoxStateType,
  MSPQCareCheckBoxStateType,
  MSPQRadioGroupStateType,
  MSPQRetirementStateType,
  VerifiedSuccess
} from './types';
import { MSPQRadioGroupOptions } from './constant';
import { History } from 'history';
import { Alert } from 'components/Alert';
import moment from 'moment';
import { DispatchResponse } from 'store/types';
import { verifyImage } from 'store/scanning/actions';
import { PreRegHeaders } from 'store/preRegistration/types';

export const getMSPQRadioGroups = (
  options: MSPQRadioGroupType[],
  radioGroupstate: MSPQRadioGroupStateType,
  checkBoxState: MSPQCheckBoxStateType
) => {
  const { age, disability, esrd } = checkBoxState;

  if (disability || esrd || !age) {
    return [];
  }

  const radioGroups = [{ ...options[0] }];

  for (let i = 1; i < options.length; i += 1) {
    const currentOption = { ...options[i] };
    const lastOption = radioGroups[i - 1];
    const lastOptionState = radioGroupstate[lastOption.key];
    if (!lastOptionState) {
      return radioGroups;
    }
    if (lastOptionState === 'no' && ['employed', 'spouseEmployed'].includes(lastOption.key)) {
      lastOption.showDate = true;
    }

    radioGroups.push(currentOption);
  }
  return radioGroups;
};

export const checkFormReady = (
  checkBoxState: MSPQCheckBoxStateType,
  careCheckBoxState: MSPQCareCheckBoxStateType
) => {
  const { age, disability, esrd } = checkBoxState;

  if (disability || esrd) {
    return true;
  }

  const values = Object.values(careCheckBoxState);
  if (age && values.some(value => value)) {
    return true;
  }

  return false;
};

export const composeHtml = (content: string) => {
  return `
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="utf-8" />
        <title>Consent Forms Generated by My Health+</title>
        <style type="text/css">
          p, li {
              line-height: 14px;
              font-size: 12px;
              margin: 8px 0;
          }

          .content ol {
            padding-left: 20px;
          }

          .content .title {
              line-height: 18px;
              font-weight: bold;
              font-size: 18px;
          }

          .content .subtitle {
              font-weight: bold;
              font-size: 14px;
          }

          .content .centering {
              text-align: center;
          }

          .content .section-container {
              white-space: nowrap;
          }

          .content .input-container {
              border: 1px black solid;
          }

          .content .section-column {
              display: inline-block;
              width: calc(50% - 10px);
              padding: 0 5px;
              white-space: normal;
          }

          hr {
              border: 1px solid lightgrey;
          }

        </style>
      </head>
      <body>
          <div class="content">
            ${content}
          </div>
      </body>
    </html>
  `;
};

type YesNo = 'Yes' | 'No';

interface Answer {
  age?: YesNo;
  disability?: YesNo;
  esrd?: YesNo;
  hospice?: YesNo;
  home?: YesNo;
  nursing?: YesNo;
  none?: YesNo;
  answer?: string | null;
}

interface QuestionListItem {
  question: string;
  answer: Answer;
}

export const generateQuestionList = (
  checkBoxState: MSPQCheckBoxStateType,
  radioGroupState: MSPQRadioGroupStateType,
  careCheckBoxState: MSPQCareCheckBoxStateType,
  retirementDateState: MSPQRetirementStateType
): QuestionListItem[] => {
  const questionList = [];
  const checkBoxQuestion = {
    question: 'Are you entitled to Medicare based on:'
  } as QuestionListItem;
  checkBoxQuestion.answer = Object.keys(checkBoxState).reduce((acc, key) => {
    return {
      ...acc,
      [key]: checkBoxState[key as keyof MSPQCheckBoxStateType] ? 'Yes' : 'No'
    } as Answer;
  }, {});
  questionList.push(checkBoxQuestion);

  if (checkBoxState.disability || checkBoxState.esrd) {
    return questionList;
  }

  MSPQRadioGroupOptions.forEach(option => {
    const question: QuestionListItem = {
      question: option.title,
      answer: { answer: radioGroupState[option.key] }
    };
    questionList.push(question);

    if (
      Object.keys(retirementDateState).includes(option.key) &&
      radioGroupState[option.key] === 'no'
    ) {
      const date = retirementDateState[option.key as keyof MSPQRetirementStateType];
      const retirementQuestion: QuestionListItem = {
        question: 'Date of retirement:',
        answer: {
          answer: date ? moment(date).format('MM/DD/YYYY') : 'N/A'
        }
      };
      questionList.push(retirementQuestion);
    }
  });

  const careCheckBoxQuestions = [
    {
      question: 'Are you receiving Hospice Care?',
      answer: { answer: careCheckBoxState.hospice ? 'Yes' : 'No' }
    },
    {
      question: 'Are you receiving Home Health Care?',
      answer: { answer: careCheckBoxState.home ? 'Yes' : 'No' }
    },
    {
      question: 'Are you receiving Skilled Nursing Facility?',
      answer: { answer: careCheckBoxState.nursing ? 'Yes' : 'No' }
    }
  ];

  return questionList.concat(careCheckBoxQuestions);
};

export const headerWithJWT = (): PreRegHeaders => {
  const data = window.sessionStorage.getItem('PRE_REG');
  if (data) {
    const parsedData = JSON.parse(data);
    return {
      Authorization: `Bearer ${parsedData.jwtToken}`,
      'x-prereg-event-id': parsedData.eventId
    };
  }
  return {};
};

export function handleError(message: string, history: History) {
  Alert.alert('Failure', message, [
    {
      text: 'OK',
      onPress: () => {
        history.push('/pre-reg/checklist');
      }
    }
  ]);
}

export async function handleVerifyUpload(
  setFile: (response: FileNameWithImage) => void,
  dispatch: DispatchResponse,
  base64?: string,
  filename?: string
): Promise<FileNameWithImage> {
  setFile({ base64 }); // Set initial so the image will be visible that is being verified.
  if (base64 && filename) {
    const res = await dispatch(verifyImage(filename, base64, headerWithJWT()));
    const resData: VerifiedSuccess = res.payload?.data as VerifiedSuccess;
    if (resData?.success) {
      const data = { base64, azureFileName: resData?.data?.azureFileName };
      setFile(data);
      return data;
    }

    Alert.alert('Invalid Image', 'Image data is invalid', undefined, {
      onClose: () => {
        setFile({ base64: '' });
      }
    });
  } else {
    setFile({ base64: '' });
  }
  return { base64 };
}
